/**
 * Tauri Bridge — détecte si l'app tourne dans un webview Tauri
 * et route vers les commandes Rust natives, sinon fallback web.
 *
 * En mode web, toutes les fonctions retournent null pour laisser
 * le code appelant utiliser le fallback navigateur standard.
 */

// ── Detection ──

export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI__" in window;
}

// ── Dynamic import (n'explose pas en mode web) ──

type InvokeFn = <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>;
let _invoke: InvokeFn | null = null;

async function getInvoke(): Promise<InvokeFn> {
  if (_invoke) return _invoke;
  const mod = await import("@tauri-apps/api/core");
  _invoke = mod.invoke as InvokeFn;
  return _invoke;
}

// ── Plans CAO ──
//
// Les conversions natives (DXF/DWG → SVG en Rust, vignette DWG, ODA,
// rastérisation resvg) ne sont plus appelées : l'application de bureau charge
// synoplix.com et importe ses plans par le service de conversion, comme le
// web. Un seul chemin, un seul moteur de fond. Les commandes Rust restent
// dans `src-tauri/` ; seuls leurs emballages TypeScript sont retirés.

// ── PDF Export ──

export interface PdfExportNativeParams {
  imageBase64: string;
  imageWidth: number;
  imageHeight: number;
  pageSize: string;
  orientation: string;
  marginMm: number;
  fitMode: string;
  showCartouche: boolean;
  cartouche: {
    title: string;
    author: string;
    company: string;
    date: string;
    revision: string;
    scale: string;
    notes: string;
  };
}

/**
 * Génère un PDF natif via printpdf (Rust) et ouvre un dialog Save-As.
 * Retourne le chemin du fichier sauvegardé ou null si annulé/erreur.
 */
export async function exportPdfNative(
  params: PdfExportNativeParams
): Promise<string | null> {
  if (!isTauri()) return null;
  try {
    const invoke = await getInvoke();
    return await invoke<string>("generate_pdf", { params });
  } catch (err) {
    console.warn("[tauri-bridge] Export PDF natif échoué, fallback web:", err);
    return null;
  }
}

/**
 * Sauvegarde un PNG via dialog Save-As natif.
 */
export async function savePngNative(
  pngBase64: string,
  defaultName: string
): Promise<string | null> {
  if (!isTauri()) return null;
  try {
    const invoke = await getInvoke();
    return await invoke<string>("save_png_to_disk", {
      pngBase64,
      defaultName,
    });
  } catch (err) {
    console.warn("[tauri-bridge] Save PNG natif échoué, fallback web:", err);
    return null;
  }
}

// ── Auto-Update ──

export interface UpdateInfo {
  version: string;
  body: string | null;
  date: string | null;
  currentVersion: string;
}

export interface DownloadProgress {
  /** Total size in bytes (null if unknown) */
  contentLength: number | null;
  /** Downloaded so far in bytes */
  downloaded: number;
}

/**
 * Vérifie s'il y a une mise à jour disponible.
 * Retourne les infos de la mise à jour ou null si à jour / pas dans Tauri.
 * Expose download progress via onProgress callback.
 */
export async function checkForUpdate(): Promise<{
  info: UpdateInfo;
  downloadAndInstall: (onProgress?: (progress: DownloadProgress) => void) => Promise<void>;
} | null> {
  if (!isTauri()) return null;
  try {
    const { check } = await import("@tauri-apps/plugin-updater");
    const update = await check();
    if (!update) return null;
    return {
      info: {
        version: update.version,
        body: update.body ?? null,
        date: update.date ?? null,
        currentVersion: update.currentVersion,
      },
      downloadAndInstall: async (onProgress) => {
        let downloaded = 0;
        let contentLength: number | null = null;
        await update.downloadAndInstall((event) => {
          if (event.event === "Started") {
            contentLength = event.data.contentLength ?? null;
            onProgress?.({ contentLength, downloaded: 0 });
          } else if (event.event === "Progress") {
            downloaded += event.data.chunkLength;
            onProgress?.({ contentLength, downloaded });
          } else if (event.event === "Finished") {
            onProgress?.({ contentLength, downloaded: contentLength ?? downloaded });
          }
        });
        const { relaunch } = await import("@tauri-apps/plugin-process");
        await relaunch();
      },
    };
  } catch (err) {
    console.warn("[tauri-bridge] Check update failed:", err);
    return null;
  }
}

// ── DevTools ──

/**
 * Ouvre/ferme les DevTools dans l'app Tauri (F12).
 */
export async function toggleDevTools(): Promise<void> {
  if (!isTauri()) return;
  try {
    const invoke = await getInvoke();
    await invoke("toggle_devtools");
  } catch (err) {
    console.warn("[tauri-bridge] toggleDevTools échoué:", err);
  }
}

/**
 * Installe le raccourci F12 pour ouvrir les DevTools dans Tauri.
 * Appeler une seule fois au démarrage de l'app.
 */
export function installDevToolsShortcut(): void {
  if (!isTauri()) return;
  document.addEventListener("keydown", (e) => {
    if (e.key === "F12") {
      e.preventDefault();
      toggleDevTools();
    }
  });
}

// ── App Version ──

/**
 * Retourne la version de l'app Tauri (depuis tauri.conf.json).
 * Retourne null si pas dans Tauri.
 */
export async function getAppVersion(): Promise<string | null> {
  if (!isTauri()) return null;
  try {
    const { getVersion } = await import("@tauri-apps/api/app");
    return await getVersion();
  } catch {
    return null;
  }
}

// ── File Dialogs ──

export interface FileFilter {
  name: string;
  extensions: string[];
}

export interface PickedFile {
  path: string;
  name: string;
}

/**
 * Ouvre un dialog natif de sélection de fichier.
 * Retourne null si pas dans Tauri ou si l'utilisateur annule.
 */
export async function pickFile(
  filters: FileFilter[]
): Promise<PickedFile | null> {
  if (!isTauri()) return null;
  try {
    const invoke = await getInvoke();
    return await invoke<PickedFile | null>("pick_file", { filters });
  } catch (err) {
    console.warn("[tauri-bridge] pickFile échoué:", err);
    return null;
  }
}

/**
 * Ouvre un dialog natif Save-As et écrit les données.
 * Retourne le chemin du fichier ou null si annulé.
 */
export async function saveFileAs(
  defaultName: string,
  data: Uint8Array,
  filters: FileFilter[]
): Promise<string | null> {
  if (!isTauri()) return null;
  try {
    const invoke = await getInvoke();
    return await invoke<string | null>("save_file_as", {
      defaultName,
      data: Array.from(data),
      filters,
    });
  } catch (err) {
    console.warn("[tauri-bridge] saveFileAs échoué:", err);
    return null;
  }
}

// ── MVR-xchange LAN (Tauri desktop only) ──

export interface MvrLanStation {
  instance: string;
  uuid: string | null;
  name: string;
  provider: string | null;
  address: string;
  port: number;
  commits: MvrLanCommit[];
}

export interface MvrLanCommit {
  FileUUID: string;
  StationUUID: string;
  FileSize: number;
  Comment?: string;
  FileName?: string;
  verMajor?: number;
  verMinor?: number;
}

export type MvrLanLogLevel = "trace" | "info" | "warn" | "error" | "critical";

export type MvrLanEvent =
  | { kind: "discovered"; station: MvrLanStation }
  | { kind: "station"; remote: string; uuid: string; name: string; provider: string; commits: MvrLanCommit[] }
  | { kind: "commit"; remote: string; commit: MvrLanCommit }
  | { kind: "binary"; remote: string; bytes: Uint8Array }
  | { kind: "removed"; instance: string }
  | { kind: "disconnect"; remote: string }
  | { kind: "log"; level: MvrLanLogLevel; msg: string; ts: number; source: "rust" };

export interface MvrLanConnectionDiag {
  remote: string;
  station_uuid: string | null;
  initiated_by_us: boolean;
  connected_since_ms: number;
  frames_in: number;
  frames_out: number;
  bytes_in: number;
  bytes_out: number;
  last_dial_error: string | null;
}

export interface MvrLanLogEntry {
  ts: number;
  level: string;
  msg: string;
}

export interface MvrLanDiagnostics {
  running: boolean;
  instance_name: string;
  local_station_uuid: string;
  local_station_name: string;
  host_ip: string;
  port: number;
  mdns_service_type: string;
  started_at_ms: number;
  self_resolved_count: number;
  mdns_events_total: number;
  last_mdns_event_at_ms: number;
  connections: MvrLanConnectionDiag[];
  discovered: MvrLanStation[];
  dial_errors: [string, string][];
  log_ring: MvrLanLogEntry[];
}

/** Start the LAN MVR-xchange daemon (mDNS publish + browse + TCP listen). */
export async function mvrxLanStart(stationName: string): Promise<string | null> {
  if (!isTauri()) return null;
  try {
    const invoke = await getInvoke();
    return await invoke<string>("mvrx_lan_start", { stationName });
  } catch (err) {
    console.warn("[tauri-bridge] mvrx_lan_start failed:", err);
    return null;
  }
}

export async function mvrxLanStop(): Promise<void> {
  if (!isTauri()) return;
  try {
    const invoke = await getInvoke();
    await invoke("mvrx_lan_stop");
  } catch (err) {
    console.warn("[tauri-bridge] mvrx_lan_stop failed:", err);
  }
}

export async function mvrxLanListStations(): Promise<MvrLanStation[]> {
  if (!isTauri()) return [];
  try {
    const invoke = await getInvoke();
    return await invoke<MvrLanStation[]>("mvrx_lan_list_stations");
  } catch {
    return [];
  }
}

export async function mvrxLanRequestFile(
  stationUuid: string,
  fileUuid: string,
  remote?: string
): Promise<void> {
  if (!isTauri()) return;
  try {
    const invoke = await getInvoke();
    // Pass both: VW occasionally sends a different StationUUID in
    // MVR_COMMIT vs MVR_JOIN_RET (looks like a project UUID), which
    // breaks pure-UUID lookup. The remote SocketAddr is the
    // authoritative connection identifier.
    await invoke("mvrx_lan_request_file", { stationUuid, fileUuid, remote });
  } catch (err) {
    console.warn("[tauri-bridge] mvrx_lan_request_file failed:", err);
  }
}

/**
 * One-shot snapshot of the Rust daemon state. Use from the
 * Diagnostics button to dump everything into DevTools console for
 * triage. Returns null when not running in Tauri or on invoke error.
 */
export async function mvrxLanDumpState(): Promise<MvrLanDiagnostics | null> {
  if (!isTauri()) return null;
  try {
    const invoke = await getInvoke();
    return await invoke<MvrLanDiagnostics>("mvrx_lan_dump_state");
  } catch (err) {
    console.error("[tauri-bridge] mvrx_lan_dump_state failed:", err);
    return null;
  }
}

/**
 * Subscribe to LAN events emitted by the Rust backend. Returns an
 * unsubscribe function that detaches every listener. No-op when not
 * inside Tauri.
 */
export async function mvrxLanSubscribe(
  onEvent: (ev: MvrLanEvent) => void
): Promise<() => void> {
  if (!isTauri()) return () => undefined;
  type Unlisten = () => void;
  let unlisteners: Unlisten[] = [];
  try {
    const { listen } = await import("@tauri-apps/api/event");
    type RawPayload = Record<string, unknown>;
    const wire = async (eventName: string, kind: MvrLanEvent["kind"]) => {
      const u = await listen<RawPayload>(eventName, (e) => {
        if (kind === "binary") {
          // The Rust side base64-encodes the file bytes so the event
          // channel stays JSON. Decode back to a Uint8Array.
          const base64 = (e.payload as { bytes?: string }).bytes ?? "";
          const remote = (e.payload as { remote?: string }).remote ?? "";
          const bin = atob(base64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          onEvent({ kind: "binary", remote, bytes });
        } else if (kind === "discovered") {
          onEvent({ kind, station: e.payload as unknown as MvrLanStation });
        } else {
          onEvent({ kind, ...(e.payload as object) } as MvrLanEvent);
        }
      });
      unlisteners.push(u);
    };
    await wire("mvrx-lan://discovered", "discovered");
    await wire("mvrx-lan://station", "station");
    await wire("mvrx-lan://commit", "commit");
    await wire("mvrx-lan://binary", "binary");
    await wire("mvrx-lan://removed", "removed");
    await wire("mvrx-lan://disconnect", "disconnect");
    await wire("mvrx-lan://log", "log");
  } catch (err) {
    console.warn("[tauri-bridge] mvrxLanSubscribe failed:", err);
  }
  return () => {
    for (const u of unlisteners) {
      try {
        u();
      } catch {
        // ignore
      }
    }
    unlisteners = [];
  };
}
